import { FACILITY_RECORD_CUSTOM_REPORT_STATIC_ROWS } from "#src/batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import useLocalization from "#src/hooks/useLocalization";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import {
  Pill,
  type HeaderType,
  type ItemBaseType,
} from "@validereinc/common-components";
import {
  FacilitySchema,
  ResourceDefinitions,
  type FacilityStatusType,
  type FacilityType,
} from "@validereinc/domain";
import {
  DateFormats,
  monthFormatter,
  toTitleCaseString,
} from "@validereinc/utilities";
import parse from "date-fns/parse";
import { get } from "es-toolkit/compat";
import React from "react";

const FacilityKeys = FacilitySchema.keyof().Enum;

export const useFacilityHeaders = <
  ItemType extends ItemBaseType = FacilityType,
>({
  prefix,
  keySubstitutions,
  sortSubstitutions,
}: {
  prefix?: string;
  keySubstitutions?: Record<string, string>;
  sortSubstitutions?: Record<string, string>;
} = {}): { isLoading: boolean; headers: Array<HeaderType<ItemType>> } => {
  const { localize, isLoading: isLoadingLocalization } = useLocalization();

  const withSortSubstitution = (key: string) =>
    sortSubstitutions?.[key] ?? (prefix ? `${prefix}.${key}` : key);
  const withKeySubstitution = (key: string) =>
    keySubstitutions?.[key] ?? (prefix ? `${prefix}.${key}` : key);

  return {
    isLoading: isLoadingLocalization,
    headers: [
      {
        key: FACILITY_RECORD_CUSTOM_REPORT_STATIC_ROWS.YEAR_MONTH,
        label: "Period",
        isSortable: true,
        renderComponent: ({ item: { year_month: yearMonth } }) =>
          yearMonth
            ? monthFormatter(
                parse(yearMonth, DateFormats.YEAR_MONTH, new Date())
              )
            : "-",
      },
      {
        key: withSortSubstitution(FacilityKeys.name),
        label: localize(ResourceDefinitions.facility.label.singular),
        isSortable: true,
        renderComponent: ({ item }) => {
          const id = get(item, withKeySubstitution(FacilityKeys.id));
          const name = get(item, withKeySubstitution(FacilityKeys.name));

          return name && id ? (
            <RoutingLink to={linkToFacilityDetail(id)}>{name}</RoutingLink>
          ) : (
            "-"
          );
        },
      },
      {
        key: withSortSubstitution(FacilityKeys.status),
        label: "Status",
        isSortable: true,
        renderComponent: ({ item }) => {
          const status = get(
            item,
            withKeySubstitution(FacilityKeys.status)
          ) as FacilityStatusType;

          return (
            <Pill variant={status === "active" ? "success" : "default"}>
              {toTitleCaseString(status, {
                intent: "ui",
                exceptions: ["oil-and-gas"],
              })}
            </Pill>
          );
        },
      },
    ],
  };
};
